import React, { useState, useEffect } from 'react';
import { FaPlus, FaFileExcel, FaSearch } from 'react-icons/fa';
import BusinessTable from '../components/businesses/BusinessTable';
import BusinessAuditModal from '../components/businesses/BusinessAuditModal';
import { supabase } from '../services/supabase';
import adminService from '../services/admin.service';
import { useAuth } from '../context/AuthContext';

function BusinessesPage() {
  const { admin } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [filteredBusinesses, setFilteredBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    fetchBusinesses();
  }, []);

  useEffect(() => {
    filterBusinesses();
  }, [searchTerm, filterStatus, businesses]);

  const fetchBusinesses = async () => {
    try {
      const { data, error } = await supabase
        .from('businesses')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Filialların tam məlumatları
      const { data: locData } = await supabase
        .from('business_locations')
        .select('*')
        .order('is_main', { ascending: false });
      const locMap = {};
      (locData || []).forEach(l => {
        if (!locMap[l.business_id]) locMap[l.business_id] = [];
        locMap[l.business_id].push(l);
      });

      // Qeydiyyat məlumatları (business_applications)
      const { data: appData } = await supabase
        .from('business_applications')
        .select('email, voen, legal_name, representative_name, representative_position, representative_phone, representative_email, founded_year, license_number, license_document_url, target_audience, website, postal_code, address, capacity, cuisine_type')
        .eq('status', 'approved');
      const appMap = {};
      (appData || []).forEach(a => { appMap[a.email] = a; });

      // Check-in sayları
      const businessesWithStats = await Promise.all(
        data.map(async (business) => {
          const { count } = await supabase
            .from('check_ins')
            .select('*', { count: 'exact', head: true })
            .eq('business_id', business.id);

          const branches = locMap[business.id] || [];
          return {
            ...business,
            total_checkins: count || 0,
            branches,
            branch_count: branches.length,
            reg_data: appMap[business.email] || null,
          };
        })
      );

      setBusinesses(businessesWithStats);
    } catch (error) {
      console.error('Error fetching businesses:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterBusinesses = () => {
    let filtered = businesses;

    // Search
    if (searchTerm) {
      filtered = filtered.filter(
        (b) =>
          b.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          b.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          b.city.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter((b) => {
        if (filterStatus === 'active') return b.is_active;
        if (filterStatus === 'inactive') return !b.is_active;
        return true;
      });
    }

    setFilteredBusinesses(filtered);
  };

  const handleKillSwitch = async (businessId, reason) => {
    try {
      // Deaktiv et
      const { error } = await supabase
        .from('businesses')
        .update({ is_active: false })
        .eq('id', businessId);

      if (error) throw error;

      // Log yaz
      await adminService.logAction(
        admin.id,
        'kill_switch',
        'business',
        businessId,
        { reason }
      );

      alert('Business uğurla deaktiv edildi!');
      fetchBusinesses();
    } catch (error) {
      console.error('Error deactivating business:', error);
      alert('Xəta baş verdi!');
    }
  };

  const handleActivate = async (business) => {
    try {
      const { error } = await supabase
        .from('businesses')
        .update({ is_active: true })
        .eq('id', business.id);

      if (error) throw error;

      // Log yaz
      await adminService.logAction(admin.id, 'activate', 'business', business.id);

      alert('Business uğurla aktivləşdirildi!');
      fetchBusinesses();
    } catch (error) {
      console.error('Error activating business:', error);
      alert('Xəta baş verdi!');
    }
  };

  const handleEdit = (business) => {
    setEditingBusiness(business);
    setEditForm({
      name: business.name || '',
      address: business.address || '',
      phone: business.phone || '',
      cuisine_type: business.cuisine_type || '',
      city: business.city || '',
      total_capacity: business.total_capacity || 0,
    });
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (!editingBusiness) return;
    try {
      const { error } = await supabase
        .from('businesses')
        .update({
          name: editForm.name,
          address: editForm.address,
          phone: editForm.phone,
          cuisine_type: editForm.cuisine_type,
          city: editForm.city,
          total_capacity: parseInt(editForm.total_capacity) || 0,
        })
        .eq('id', editingBusiness.id);

      if (error) throw error;

      await adminService.logAction(admin.id, 'edit_business', 'business', editingBusiness.id, { changes: editForm });
      alert('Business uğurla yeniləndi!');
      setShowEditModal(false);
      setEditingBusiness(null);
      fetchBusinesses();
    } catch (error) {
      console.error('Error updating business:', error);
      alert('Xəta baş verdi: ' + error.message);
    }
  };

  const handleDelete = async (business) => {
    const confirmed = window.confirm(
      `"${business.name}" hesabını tamamilə silmək istəyirsiniz?\n\nBu əməliyyat GERİ ALINMAZ:\n• Bütün filiallar silinəcək\n• Bütün menecerlər silinəcək\n• Müştəri məlumatları silinəcək\n\nDavam etmək üçün OK basın.`
    );
    if (!confirmed) return;

    try {
      // RPC funksiyası SECURITY DEFINER ilə RLS-i keçərək sil
      const { error } = await supabase.rpc('delete_business_cascade', {
        p_business_id: business.id,
      });
      if (error) throw error;

      // 5. Log yaz
      await adminService.logAction(admin.id, 'delete_business', 'business', business.id, {
        name: business.name,
        email: business.email,
      });

      alert(`✅ "${business.name}" hesabı silindi.`);
      fetchBusinesses();
    } catch (err) {
      console.error('Delete error:', err);
      alert('❌ Xəta: ' + err.message);
    }
  };

  const handleViewStats = (business) => {
    setSelectedBusiness(business);
    setShowAuditModal(true);
  };

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner}></div>
        <p>Yüklənir...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>🏢 Business Management</h1>
          <p style={styles.subtitle}>Bütün business-ləri idarə edin</p>
        </div>
        <div style={styles.headerActions}>
          <button style={styles.btnExport}>
            <FaFileExcel /> Export Excel
          </button>
          <button style={styles.btnAdd}>
            <FaPlus /> Yeni Business
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={styles.filters}>
        <div style={styles.searchBox}>
          <FaSearch style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Ad, email və ya şəhər ilə axtarış..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
        </div>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={styles.filterSelect}
        >
          <option value="all">Hamısı</option>
          <option value="active">Aktiv</option>
          <option value="inactive">Deaktiv</option>
        </select>
      </div>

      {/* Stats */}
      <div style={styles.stats}>
        <div style={styles.statCard}>
          <span style={styles.statLabel}>Ümumi:</span>
          <span style={styles.statValue}>{businesses.length}</span>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statLabel}>Aktiv:</span>
          <span style={{ ...styles.statValue, color: '#4CAF50' }}>
            {businesses.filter((b) => b.is_active).length}
          </span>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statLabel}>Deaktiv:</span>
          <span style={{ ...styles.statValue, color: '#F44336' }}>
            {businesses.filter((b) => !b.is_active).length}
          </span>
        </div>
      </div>

      {/* Table */}
      <BusinessTable
        businesses={filteredBusinesses}
        onEdit={handleEdit}
        onKillSwitch={handleKillSwitch}
        onActivate={handleActivate}
        onViewStats={handleViewStats}
        onDelete={handleDelete}
      />

      {/* Audit Modal */}
      {showAuditModal && selectedBusiness && (
        <BusinessAuditModal
          business={selectedBusiness}
          onClose={() => {
            setShowAuditModal(false);
            setSelectedBusiness(null);
          }}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && editingBusiness && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalBox}>
            <h2 style={styles.modalTitle}>✏️ Business Düzəliş: {editingBusiness.name}</h2>
            <div style={styles.modalFields}>
              {[
                { key: 'name', label: 'Ad' },
                { key: 'address', label: 'Ünvan' },
                { key: 'phone', label: 'Telefon' },
                { key: 'cuisine_type', label: 'Mətbəx Növü' },
                { key: 'city', label: 'Şəhər' },
                { key: 'total_capacity', label: 'Ümumi Tutum', type: 'number' },
              ].map(({ key, label, type }) => (
                <div key={key} style={styles.modalField}>
                  <label style={styles.modalLabel}>{label}</label>
                  <input
                    type={type || 'text'}
                    value={editForm[key]}
                    onChange={(e) => setEditForm(prev => ({ ...prev, [key]: e.target.value }))}
                    style={styles.modalInput}
                  />
                </div>
              ))}
            </div>
            <div style={styles.modalActions}>
              <button onClick={() => { setShowEditModal(false); setEditingBusiness(null); }} style={styles.btnCancel}>Ləğv Et</button>
              <button onClick={handleEditSave} style={styles.btnSave}>Yadda Saxla</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '1600px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '30px',
    flexWrap: 'wrap',
    gap: '20px',
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#1A1A2E',
    marginBottom: '5px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#999',
  },
  headerActions: {
    display: 'flex',
    gap: '15px',
  },
  btnExport: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 20px',
    backgroundColor: '#4CAF50',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  btnAdd: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 20px',
    backgroundColor: '#FF6B35',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  filters: {
    display: 'flex',
    gap: '15px',
    marginBottom: '20px',
    flexWrap: 'wrap',
  },
  searchBox: {
    flex: 1,
    minWidth: '300px',
    position: 'relative',
  },
  searchIcon: {
    position: 'absolute',
    left: '15px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#999',
  },
  searchInput: {
    width: '100%',
    padding: '12px 15px 12px 45px',
    fontSize: '14px',
    border: '2px solid #E0E0E0',
    borderRadius: '8px',
    outline: 'none',
  },
  filterSelect: {
    padding: '12px 15px',
    fontSize: '14px',
    border: '2px solid #E0E0E0',
    borderRadius: '8px',
    outline: 'none',
    cursor: 'pointer',
    backgroundColor: '#fff',
  },
  stats: {
    display: 'flex',
    gap: '15px',
    marginBottom: '20px',
  },
  statCard: {
    flex: 1,
    padding: '15px 20px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statLabel: {
    fontSize: '14px',
    color: '#666',
    fontWeight: '600',
  },
  statValue: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#1A1A2E',
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '60vh',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '5px solid #f3f3f3',
    borderTop: '5px solid #FF6B35',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalBox: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '30px',
    width: '520px',
    maxWidth: '90vw',
    boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#1A1A2E',
    marginBottom: '20px',
  },
  modalFields: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '15px',
    marginBottom: '25px',
  },
  modalField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  modalLabel: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#666',
  },
  modalInput: {
    padding: '10px 12px',
    fontSize: '14px',
    border: '2px solid #E0E0E0',
    borderRadius: '8px',
    outline: 'none',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
  },
  btnCancel: {
    padding: '10px 20px',
    backgroundColor: '#9E9E9E',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  btnSave: {
    padding: '10px 20px',
    backgroundColor: '#4CAF50',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};

export default BusinessesPage;